import { useMemo, useRef, useState } from "react";

import {
  ALL_USERS,
  AVATAR_COLORS,
  C,
  CUST_PRIO_STYLE,
  DEAL_CAT_STYLE,
  DEAL_TYPE_STYLE,
  DEPT_COLORS,
  EMP_MAP,
  FINANCIAL_MAP,
  GROWTH_MAP,
  P_MAP,
  PAYMENT_MAP,
  SERVICE_MAP,
  STATUS_MAP,
  TASK_PRIO_STYLE,
  TASK_STATUS_STYLE,
  parseMentions,
} from "../../constants";
import { getInit, priorityLabel } from "../../utils";

// Convert SCREAMING_SNAKE_CASE ("ON_TRACK") into a human label ("On Track"). Leaves
// already-title-case values ("At Risk") alone. Safe on null/undefined.
function humanizeStatus(v) {
  if (typeof v !== "string" || !v) return v;
  if (!/^[A-Z_]+$/.test(v)) return v; // already human
  return v.toLowerCase().replace(/(^|_)([a-z])/g, (_, prefix, ch) => (prefix ? " " : "") + ch.toUpperCase());
}

export function Chip({ status }) {
  // Normalise underscore vs space so Prisma enums (e.g. TRANSFORMATION_SCAN) still resolve.
  const alt = typeof status === "string" ? status.replace(/_/g, " ") : status;
  const s =
    STATUS_MAP[status] || STATUS_MAP[alt] ||
    SERVICE_MAP[status] || SERVICE_MAP[alt] ||
    PAYMENT_MAP[status] || PAYMENT_MAP[alt] ||
    GROWTH_MAP[status] || GROWTH_MAP[alt] ||
    FINANCIAL_MAP[status] || FINANCIAL_MAP[alt] ||
    EMP_MAP[status] || EMP_MAP[alt] ||
    { fg: "#c4d4ee", bg: "rgba(143,164,200,.14)" };
  return (
    <span className="chip" style={{ color: s.fg, background: s.bg, border: `1px solid ${s.fg}22` }}>
      <span className="dot" style={{ background: s.fg }} />
      {humanizeStatus(status)}
    </span>
  );
}

export function PBadge({ score }) {
  const l = priorityLabel(score);
  const m = P_MAP[l];
  return (
    <span className="chip" style={{ color: m.fg, background: m.bg, border: `1px solid ${m.fg}22`, fontSize: "9.5px" }}>
      <span className="dot" style={{ background: m.fg }} />
      {m.label}
    </span>
  );
}

export function DBadge({ dept }) {
  const s = DEPT_COLORS[dept] || { fg: C.sub, bg: "rgba(143,164,200,.1)" };
  return (
    <span className="chip" style={{ color: s.fg, background: s.bg, border: `1px solid ${s.fg}22`, fontSize: "9.5px", textTransform: "none", letterSpacing: 0 }}>
      {dept}
    </span>
  );
}

export function TSChip({ status }) {
  const s = TASK_STATUS_STYLE[status] || TASK_STATUS_STYLE["To Do"];
  return (
    <span className="chip" style={{ color: s.fg, background: s.bg, border: `1px solid ${s.fg}22`, fontSize: "9.5px" }}>
      <span className="dot" style={{ background: s.fg }} />
      {status}
    </span>
  );
}

export function TPChip({ priority }) {
  const s = TASK_PRIO_STYLE[priority] || TASK_PRIO_STYLE["Low"];
  return <span className="chip" style={{ color: s.fg, background: s.bg, border: `1px solid ${s.fg}22`, fontSize: "9px" }}>{priority}</span>;
}

export function CPTag({ cp }) {
  if (!cp || cp === "Standard") return null;
  const s = CUST_PRIO_STYLE[cp];
  return <span className="chip" style={{ color: s.fg, background: s.bg, border: `1px solid ${s.fg}22`, fontSize: "9px" }}>{s.icon} {cp}</span>;
}

export function Avt({ initials, src, size = 30, style = {}, className = "" }) {
  const bg = AVATAR_COLORS[initials] || "rgb(64,117,193)";
  const avatarClass = className ? `avatar ${className}` : "avatar";
  if (src) {
    return (
      <img
        src={src}
        alt={initials}
        className={avatarClass}
        style={{ width: size, height: size, objectFit: "cover", flexShrink: 0, ...style }}
        onError={(e) => {
          e.currentTarget.style.display = "none";
          e.currentTarget.nextSibling.style.display = "flex";
        }}
      />
    );
  }
  return <div className={avatarClass} style={{ width: size, height: size, background: bg, fontSize: size * 0.34, ...style }}>{initials}</div>;
}

export function KpiCard({ label, value, sub, trend, up, onClick, accent }) {
  return (
    <div className={`kpi${onClick ? " click" : ""}`} onClick={onClick} style={accent ? { borderTopColor: accent, borderTopWidth: 2 } : {}}>
      <div className="kpi-lbl">{label}</div>
      <div className="kpi-val" style={accent ? { color: accent } : {}}>{value}</div>
      {trend && (
        <div className="kpi-trend" style={{ color: up ? C.green : C.red }}>
          <span>{up ? "▲" : "▼"}</span>
          <span>{trend}</span>
        </div>
      )}
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

export function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div className="toast-wrap">
      <div className="toast">
        <span style={{ fontSize: 18 }}>✓</span>
        <div>
          <div className="toast-title">{toast.title}</div>
          {toast.sub && <div className="toast-sub">{toast.sub}</div>}
        </div>
      </div>
    </div>
  );
}

export function MentionInput({ value, onChange, placeholder, rows = 3, onSubmit, className = "" }) {
  const [mentionQ, setMQ] = useState(null);
  const [selIdx, setSel] = useState(0);
  const taRef = useRef(null);
  const filtered = mentionQ !== null ? ALL_USERS.filter((u) => u.toLowerCase().includes(mentionQ.toLowerCase())) : [];

  function handleChange(e) {
    const txt = e.target.value;
    onChange(txt);
    const cur = e.target.selectionStart;
    const before = txt.slice(0, cur);
    const m = before.match(/@([\w ]*)$/);
    if (m) {
      setMQ(m[1]);
      setSel(0);
    } else {
      setMQ(null);
    }
  }

  function insertMention(user) {
    const ta = taRef.current;
    const cur = ta.selectionStart;
    const before = value.slice(0, cur);
    const after = value.slice(cur);
    const start = before.lastIndexOf("@");
    const nv = before.slice(0, start) + "@" + user + " " + after;
    onChange(nv);
    setMQ(null);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + user.length + 2, start + user.length + 2);
    }, 10);
  }

  function handleKey(e) {
    if (mentionQ !== null && filtered.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSel((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSel((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && filtered.length > 0) {
        e.preventDefault();
        insertMention(filtered[selIdx]);
        return;
      } else if (e.key === "Escape") {
        setMQ(null);
      }
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && onSubmit) onSubmit();
  }

  const mentions = useMemo(() => parseMentions(value), [value]);

  return (
    <div className="mention-wrap">
      {mentionQ !== null && filtered.length > 0 && (
        <div className="mention-dd">
          <div className="mention-hint">Mention a teammate</div>
          {filtered.map((u, i) => (
            <div key={u} className={`mention-item${i === selIdx ? " msel" : ""}`} onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}>
              <Avt initials={getInit(u)} size={22} />
              <span>{u}</span>
            </div>
          ))}
        </div>
      )}
      <textarea ref={taRef} className={`mention-ta${className ? ` ${className}` : ""}`} rows={rows} placeholder={placeholder} value={value} onChange={handleChange} onKeyDown={handleKey} />
      {mentions.length > 0 && (
        <div style={{ marginTop: 5, display: "flex", flexWrap: "wrap" }}>
          {mentions.map((m) => <span key={m} className="mention-badge"><Avt initials={getInit(m)} size={14} />@{m}</span>)}
        </div>
      )}
    </div>
  );
}

export function RenderMentions({ text }) {
  const parts = text.split(/(@[\w ]+)/g);
  return <>{parts.map((p, i) => (/^@/.test(p) ? <strong key={i} className="mention-highlight">{p}</strong> : p))}</>;
}

export function DealCatChip({ cat }) {
  const s = DEAL_CAT_STYLE[cat] || { fg: C.sub, bg: "rgba(143,164,200,.1)" };
  return <span className="deal-cat" style={{ color: s.fg, background: s.bg, border: `1px solid ${s.fg}22` }}>{cat}</span>;
}

export function DealTypeBadge({ type }) {
  const s = DEAL_TYPE_STYLE[type] || { fg: C.sub, bg: "rgba(143,164,200,.1)" };
  return <span className="deal-cat" style={{ color: s.fg, background: s.bg, border: `1px solid ${s.fg}22` }}>{type}</span>;
}
